import {
  type Analytics,
  type EventParams,
  getAnalytics,
  isSupported,
  logEvent,
  setUserId,
} from "firebase/analytics";

import { app } from "./app";
import { auth } from "./auth";

// GA4 cuts event parameter values off at 100 characters.
const MAX_PARAM_LENGTH = 100;

// Keep the emulators and tests out of the real reports, and skip browsers
// (or blockers) that can't run Analytics at all.
const analytics: Promise<Analytics | undefined> =
  import.meta.env.VITE_USE_EMULATORS === "true" || import.meta.env.MODE === "test"
    ? Promise.resolve(undefined)
    : isSupported()
        .then((supported) => (supported ? getAnalytics(app) : undefined))
        .catch(() => undefined);

function describe(error: unknown): string {
  if (error instanceof Error) return `${error.name}: ${error.message}`;
  if (typeof error === "string") return error;
  try {
    return JSON.stringify(error) ?? String(error);
  } catch {
    return String(error);
  }
}

/** Where in our code the error came from, e.g. "ShopPage-abc.js:12:34". */
function origin(error: unknown): string | undefined {
  if (!(error instanceof Error) || !error.stack) return undefined;
  const frame = /\/([^/\s]+:\d+:\d+)\)?$/m.exec(error.stack);
  return frame?.[1];
}

/** Logs an event to Analytics, if it's running. */
export function trackEvent(name: string, params?: EventParams) {
  void analytics.then((instance) => {
    if (instance) logEvent(instance, name, params);
  });
}

/** Logs an error to Analytics as a GA4 `exception` event. */
export function logError(error: unknown, fatal = false) {
  trackEvent("exception", {
    description: describe(error).slice(0, MAX_PARAM_LENGTH),
    fatal,
    error_origin: origin(error)?.slice(0, MAX_PARAM_LENGTH),
    page_path: window.location.pathname.slice(0, MAX_PARAM_LENGTH),
  });
}

/**
 * Starts Analytics, ties its events to the signed-in user, and logs any error
 * nothing else caught. Errors that reach a route's ErrorPage are logged there.
 */
export function startAnalytics() {
  auth.onAuthStateChanged(function (user) {
    void analytics.then((instance) => {
      if (instance) setUserId(instance, user?.uid ?? null);
    });
  });

  window.addEventListener("error", function (event) {
    logError(event.error ?? event.message, true);
  });
  window.addEventListener("unhandledrejection", function (event) {
    logError(event.reason);
  });
}
